import Axios from 'axios';
import { useMutation } from 'react-query';

import apiUrls from 'base/api/urls';
import {
  SetQueryDataFn,
  useDetailsQuery,
  useListQuery,
  useSetQueryData,
} from 'base/api/hooks';
import { Collection, CollectionListItem } from 'base/api/types';
import { StandardError } from 'base/api/errors';
import { composeUrl } from 'common/utils';
import { PK } from 'common/types';

export const useCollectionsList = () => {
  return useListQuery<CollectionListItem>(apiUrls.COLLECTIONS.LIST);
};

export const useCollectionDetails = (pk?: PK) => {
  return useDetailsQuery<Collection>({
    queryKey: [apiUrls.COLLECTIONS.DETAILS, { pk }],
    pk,
  });
};

interface CollectionVars {
  collectionPk: PK;
}

interface CollectionItemsVars extends CollectionVars {
  items_pks: PK[];
}

export const useCollectionItemsUpdate = () => {
  const setQueryData = useSetQueryData();

  return useMutation<unknown, StandardError, CollectionItemsVars>(
    ({ collectionPk, ...data }) =>
      Axios.put(
        composeUrl(apiUrls.COLLECTIONS.ITEMS, {
          params: { collectionPk },
        }),
        data,
      ),
    {
      onSuccess: (data, { collectionPk, items_pks }) => {
        setQueryData(
          [apiUrls.COLLECTIONS.DETAILS, { pk: collectionPk }],
          updateCollectionItemsCache({ items_pks }),
          true,
        );
      },
    },
  );
};

type UpdateCollectionItemsCacheArguments = Pick<CollectionItemsVars, 'items_pks'>;

const updateCollectionItemsCache = ({
  items_pks,
}: UpdateCollectionItemsCacheArguments): SetQueryDataFn<Collection> => (oldData) => {
  if (!oldData) {
    throw new Error('Expected oldData to be defined when updating items.');
  }

  const updatedItems = items_pks
    .map((itemPk) => oldData.items.find((item) => item === itemPk))
    .filter((item): item is PK => Boolean(item));

  return {
    ...oldData,
    items: updatedItems,
  };
};
